import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, clearMsalCache } from "../../authConfig";
import {
  InteractionStatus,
} from "@azure/msal-browser";
import {
  Box,
  Alert,
  Snackbar,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  Fade,
  Grow,
} from "@mui/material";
import Q8Button from "../../components/Q8Button";
import { Microsoft as MicrosoftIcon } from "@mui/icons-material";

const LoginPage = () => {
  const { instance, inProgress } = useMsal();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [animation, setAnimation] = useState(false);

  // Funzione per pulire il cookie di autenticazione
  const clearAuthCookie = () => {
    document.cookie =
      "Q8TechAuth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  };

  useEffect(() => {
    // Trigger animation after component mount
    setTimeout(() => setAnimation(true), 100);

    // Controlla se è la prima apertura dopo un logout
    const forceNewLogin = sessionStorage.getItem("forceNewLogin") === "true";
    if (forceNewLogin) {
      // Rimuovi il flag dopo averlo controllato
      sessionStorage.removeItem("forceNewLogin");

      // Pulisci completamente lo stato MSAL
      clearMsalCache();
      clearAuthCookie();

      // Rimuovi qualsiasi hash nell'URL che potrebbe interferire
      if (window.location.hash) {
        window.history.replaceState(
          null,
          document.title,
          window.location.pathname
        );
      }
    }
  }, []);

  // Funzione per l'acquisizione del token: chiede sempre di selezionare l'utente,
  // anche se c'è già un account in cache (niente login silenzioso/automatico, niente popup)
  const acquireToken = async () => {
    try {
      if (window.location.hash) {
        window.history.replaceState(
          null,
          document.title,
          window.location.pathname + window.location.search
        );
      }

      await instance.loginRedirect({
        ...loginRequest,
        prompt: "select_account",
      });
      // Questo causerà un reindirizzamento, quindi la funzione non continuerà
      return null;
    } catch (error) {
      console.error("Token acquisition failed:", error);
      throw error;
    }
  };

  // Gestisci il processo di login
  const handleLogin = async () => {
    // Non procedere se l'autenticazione è già in corso
    if (inProgress !== InteractionStatus.None) {
      setError("Processo di autenticazione già in corso. Per favore attendi.");
      return;
    }

    setIsLoading(true);
    setError(null);

    // Pulisci solo il cookie dell'app, NON la cache MSAL.
    // Cancellare la cache MSAL (chiavi msal.*) prima di acquireToken distrugge
    // lo stato interno di MSAL e i token cached, costringendo a nuovi popup ripetuti.
    clearAuthCookie();

    // Ripulisci l'URL da eventuali parametri di autenticazione residui
    if (window.location.hash && window.location.hash.length > 0) {
      window.history.replaceState(
        null,
        document.title,
        window.location.pathname
      );
    }

    try {
      // Avvia il redirect verso AAD; il componente non continuerà oltre questo punto
      // perché il browser navigherà via. L'esito viene elaborato in App.js al ritorno.
      await acquireToken();
    } catch (error) {
      console.error("Login failed:", error);

      // Log dettagliato dell'errore per facilitare il debug
      if (error.errorCode) {
        console.error("Error code:", error.errorCode);
      }
      if (error.errorMessage) {
        console.error("Error message:", error.errorMessage);
      }
      if (error.stack) {
        console.error("Error stack:", error.stack);
      }

      // Pulizia aggiuntiva in caso di errore
      clearMsalCache();

      // Imposta il messaggio di errore
      setError(
        "Errore durante il login: " + (error.message || "errore sconosciuto")
      );

      // In caso di errori specifici con il popup, prova a ricaricare la pagina
      if (
        error.errorCode === "user_cancelled" ||
        error.errorCode === "hash_empty_error"
      ) {
        setTimeout(() => {
          window.location.reload();
        }, 2000); // Ritardo per mostrare il messaggio di errore prima del ricaricamento
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
        padding: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: 0.1,
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "white",
            filter: "blur(40px)",
            animation: "float 8s ease-in-out infinite",
            "@keyframes float": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-20px)" },
              "100%": { transform: "translateY(0px)" },
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "15%",
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: "white",
            filter: "blur(50px)",
            animation: "float2 10s ease-in-out infinite",
            "@keyframes float2": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-30px)" },
              "100%": { transform: "translateY(0px)" },
            },
          }}
        />
      </Box>

      <Grow in={animation} timeout={1000}>
        <Paper
          elevation={10}
          sx={{
            padding: 5,
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.2)",
            zIndex: 1,
          }}
        >
          <Fade in={animation} timeout={1500}>
            <Box
              component="img"
              sx={{
                height: 110,
                mb: 4,
                objectFit: "contain",
                transition: "transform 0.3s ease",
                // "&:hover": {
                //   transform: "scale(1.05)",
                // },
              }}
              alt="Q8 Logo"
              src="/Logo2-BKWYCREK.png"
            />
          </Fade>

          <Typography
            variant="h5"
            component="h1"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: theme.palette.primary.main,
              textAlign: "center",
            }}
          >
            Benvenuto nel Portale Q8 TechExpenses
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            Accedi con il tuo account Microsoft per continuare
          </Typography>

          <Q8Button
            variant="primary"
            label={isLoading ? "Accesso in corso..." : "Accedi con Microsoft"}
            onClick={handleLogin}
            disabled={isLoading || inProgress !== InteractionStatus.None}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <MicrosoftIcon />
              )
            }
            sx={{
              width: "100%",
              py: 1.5,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover:not(:disabled)": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
              },
              "&:active:not(:disabled)": {
                transform: "translateY(0)",
              },
            }}
          />
        </Paper>
      </Grow>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          variant="filled"
          elevation={6}
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;
